import { SfxToggle, useSfx } from '@slingu/ui';
import { useEffect, useRef, useState } from 'react';

import { drawBackground, drawSlingu, drawGround, sizeCanvas } from './slingu-draw.js';
import { commitScore, getHighScore } from './highscores.js';
import { LEGACY_POOL, blockShuffle, type FlappyPair } from './vocabulary.js';

const HIGH_SCORE_ID = 'word-jumper';

export type WordJumperProps = {
  onExit: (result: { score: number; caught: number; missed: number; xp: number }) => void;
};

type Phase = 'menu' | 'playing' | 'over';

// --- Portrait world -----------------------------------------------------
const WORLD_W = 480;
const WORLD_H = 760;
const GROUND_Y = WORLD_H - 80;
const GROUND_H = WORLD_H - GROUND_Y;

const PLAYER_X = 140;
const PLAYER_R = 24;

const GRAVITY = 1500;
const JUMP_V = -600;
const SCROLL = 180;

const PLATFORM_W = 140;
const PLATFORM_H = 18;
const HI_Y = GROUND_Y - 200;
const LO_Y = GROUND_Y - 90;
const SPAWN_INTERVAL = 2.3;
const HIT_GRACE = 0.7;

type Platform = {
  id: string;
  pairId: string;
  x: number;
  y: number;
  label: string;
  isCorrect: boolean;
  consumed: boolean;
};

/**
 * Word Jumper — side-scroller com plataformas flutuantes. O Slingu cai sob
 * gravidade e o jogador toca na tela pra pular (Flappy-style). Plataformas
 * vêm pela direita em 2 alturas (alta/baixa), cada uma com uma palavra em
 * inglês. Pouse na plataforma com a tradução correta pra ganhar XP; pousar
 * na errada faz o Slingu explodir. 3 hearts totais.
 */
export function WordJumper({ onExit }: WordJumperProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);

  const sfx = useSfx();
  const sfxRef = useRef(sfx);
  sfxRef.current = sfx;
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;

  const [phase, setPhase] = useState<Phase>('menu');
  const [hud, setHud] = useState({ score: 0, caught: 0, missed: 0, hearts: 3 });
  const [highScore, setHighScore] = useState<number>(() => getHighScore(HIGH_SCORE_ID));
  const [newRecord, setNewRecord] = useState<boolean>(false);
  const promptRef = useRef<string>('');

  const gameRef = useRef({
    scrollX: 0,
    playerY: WORLD_H / 2,
    playerVy: 0,
    platforms: [] as Platform[],
    queue: [] as FlappyPair[],
    spawnTimer: 1.0,
    pairCounter: 0,
    wingPhase: 0,
    blinkT: 2 + Math.random() * 2,
    blink: 0,
    particles: [] as { x: number; y: number; vx: number; vy: number; life: number; size: number; color: string }[],
    score: 0,
    caught: 0,
    missed: 0,
    hearts: 3,
    alive: true,
    hitCooldown: 0,
    firstInput: false,
    shake: 0,
  });

  const startedRef = useRef(false);

  useEffect(() => {
    if (phase !== 'playing') return;
    if (startedRef.current) return;
    startedRef.current = true;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const g = gameRef.current;
    g.scrollX = 0;
    g.playerY = WORLD_H / 2;
    g.playerVy = 0;
    g.platforms = [];
    g.queue = blockShuffle(LEGACY_POOL, 50);
    g.spawnTimer = 1.0;
    g.pairCounter = 0;
    g.particles = [];
    g.score = 0;
    g.caught = 0;
    g.missed = 0;
    g.hearts = 3;
    g.alive = true;
    g.hitCooldown = 0;
    g.firstInput = false;
    g.shake = 0;
    setHud({ score: 0, caught: 0, missed: 0, hearts: 3 });
    promptRef.current = 'Toque pra começar a pular';

    let disposed = false;
    function resize() {
      if (!canvas) return;
      sizeCanvas(canvas, WORLD_W, WORLD_H);
    }
    resize();
    window.addEventListener('resize', resize);

    function flap() {
      if (!g.alive) return;
      g.playerVy = JUMP_V;
      g.firstInput = true;
      sfxRef.current.play('jump');
    }
    function onPointerDown(e: PointerEvent) { e.preventDefault(); flap(); }
    function onKey(e: KeyboardEvent) {
      if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') {
        e.preventDefault(); flap();
      }
    }
    canvas.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKey);

    let lastT = performance.now();
    function frame(nowMs: number) {
      if (disposed) return;
      const dt = Math.min(0.05, (nowMs - lastT) / 1000);
      lastT = nowMs;
      update(dt);
      render();
      rafRef.current = requestAnimationFrame(frame);
    }

    function update(dt: number) {
      if (!g.alive) return;

      if (g.firstInput) {
        g.playerVy += GRAVITY * dt;
        g.playerY += g.playerVy * dt;
      }

      // Ground
      if (g.playerY > GROUND_Y - PLAYER_R) {
        g.playerY = GROUND_Y - PLAYER_R;
        g.playerVy = 0;
      }
      if (g.playerY < 0) { g.playerY = 0; g.playerVy = Math.max(g.playerVy, 60); }

      g.scrollX += SCROLL * dt * 0.5;
      g.wingPhase += dt * (8 + Math.max(0, -g.playerVy / 40));
      g.blinkT -= dt;
      if (g.blinkT <= 0) { g.blink = 1; g.blinkT = 2 + Math.random() * 3; }
      else if (g.blink > 0) g.blink = Math.max(0, g.blink - dt * 8);
      if (g.hitCooldown > 0) g.hitCooldown -= dt;
      if (g.shake > 0) g.shake = Math.max(0, g.shake - dt * 30);

      // Particles
      g.particles = g.particles.filter((p) => p.life > 0);
      for (const p of g.particles) {
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += 120 * dt;
        p.life -= dt;
      }

      if (g.firstInput) {
        g.spawnTimer -= dt;
        if (g.spawnTimer <= 0) {
          if (g.queue.length === 0) {
            if (g.platforms.length === 0) endGame();
          } else {
            spawnPair(g.queue.shift()!);
            g.spawnTimer = SPAWN_INTERVAL;
          }
        }
      }

      // Move platforms + collisions
      for (const pl of g.platforms) {
        pl.x -= SCROLL * dt;
        if (pl.consumed) continue;
        // Check if Slingu is landing on this platform
        if (g.hitCooldown <= 0 && g.playerVy > 0) {
          const inCol = PLAYER_X + PLAYER_R - 4 > pl.x && PLAYER_X - PLAYER_R + 4 < pl.x + PLATFORM_W;
          const landing = g.playerY + PLAYER_R >= pl.y && g.playerY + PLAYER_R <= pl.y + PLATFORM_H + 10;
          if (inCol && landing) {
            onLanded(pl);
          }
        }
      }
      g.platforms = g.platforms.filter((p) => p.x + PLATFORM_W > -40);

      // HUD publish
      setHud((prev) => {
        const score = Math.floor(g.score);
        if (prev.score === score && prev.caught === g.caught && prev.missed === g.missed && prev.hearts === g.hearts) return prev;
        return { score, caught: g.caught, missed: g.missed, hearts: g.hearts };
      });
    }

    function spawnPair(pair: FlappyPair) {
      g.pairCounter += 1;
      const id = `p${g.pairCounter}`;
      const correctHigh = Math.random() < 0.5;
      g.platforms.push({
        id: id + 'hi',
        pairId: id,
        x: WORLD_W + 20,
        y: HI_Y,
        label: correctHigh ? pair.correct : pair.wrong,
        isCorrect: correctHigh,
        consumed: false,
      });
      g.platforms.push({
        id: id + 'lo',
        pairId: id,
        x: WORLD_W + 20,
        y: LO_Y,
        label: correctHigh ? pair.wrong : pair.correct,
        isCorrect: !correctHigh,
        consumed: false,
      });
      promptRef.current = `Pouse em: "${pair.promptPt ?? pair.correct}"`;
    }

    function onLanded(pl: Platform) {
      pl.consumed = true;
      for (const sib of g.platforms) if (sib.pairId === pl.pairId) sib.consumed = true;
      if (pl.isCorrect) {
        // Bounce on top, + XP
        g.playerY = pl.y - PLAYER_R;
        g.playerVy = JUMP_V * 0.8;
        g.score += 18;
        g.caught += 1;
        sfxRef.current.play('xp-gain');
        sfxRef.current.play('land');
        // Sparkles
        for (let i = 0; i < 8; i++) {
          const a = Math.random() * Math.PI * 2;
          g.particles.push({
            x: pl.x + PLATFORM_W / 2, y: pl.y, vx: Math.cos(a) * 140, vy: Math.sin(a) * 140,
            life: 0.6, size: 3, color: 'rgb(255, 220, 120)',
          });
        }
      } else {
        // EXPLODE
        g.missed += 1;
        g.hearts -= 1;
        g.hitCooldown = HIT_GRACE;
        g.shake = 18;
        g.playerVy = JUMP_V * 0.7;
        sfxRef.current.play('hit');
        sfxRef.current.play('heart-loss');
        const palette = ['rgb(255, 220, 60)', 'rgb(255, 150, 40)', 'rgb(240, 70, 40)'];
        for (let i = 0; i < 24; i++) {
          const a = (i / 24) * Math.PI * 2;
          const s = 160 + Math.random() * 220;
          g.particles.push({
            x: PLAYER_X, y: g.playerY, vx: Math.cos(a) * s, vy: Math.sin(a) * s,
            life: 0.7, size: 4 + Math.random() * 4, color: palette[i % 3]!,
          });
        }
        if (g.hearts <= 0) endGame();
      }
    }

    function endGame() {
      if (!g.alive) return;
      g.alive = false;
      sfxRef.current.play('game-over');
      const finalScore = Math.floor(g.score);
      const result = commitScore(HIGH_SCORE_ID, finalScore);
      setHighScore(result.best);
      setNewRecord(result.isNew);
      setPhase('over');
    }

    function render() {
      const ctx = canvas!.getContext('2d');
      if (!ctx) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const sx = (Math.random() - 0.5) * g.shake;
      const sy = (Math.random() - 0.5) * g.shake;
      ctx.setTransform(dpr, 0, 0, dpr, sx * dpr, sy * dpr);

      drawBackground(ctx, WORLD_W, WORLD_H, GROUND_Y, g.scrollX, g.scrollX * 0.5);
      drawGround(ctx, WORLD_W, GROUND_Y, GROUND_H, g.scrollX);

      // Platforms
      for (const pl of g.platforms) {
        if (pl.consumed) continue;
        const color = pl.isCorrect ? 'rgb(88, 204, 2)' : 'rgb(238, 85, 85)';
        const shade = pl.isCorrect ? 'rgb(58, 136, 18)' : 'rgb(180, 40, 40)';
        // Platform body
        ctx.fillStyle = shade;
        roundRect(ctx, pl.x, pl.y + 4, PLATFORM_W, PLATFORM_H, 5);
        ctx.fill();
        ctx.fillStyle = color;
        roundRect(ctx, pl.x, pl.y, PLATFORM_W, PLATFORM_H, 5);
        ctx.fill();
        // Word label above
        ctx.save();
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        roundRect(ctx, pl.x + 10, pl.y - 36, PLATFORM_W - 20, 28, 14);
        ctx.fill();
        ctx.strokeStyle = shade;
        ctx.lineWidth = 2;
        roundRect(ctx, pl.x + 10, pl.y - 36, PLATFORM_W - 20, 28, 14);
        ctx.stroke();
        ctx.fillStyle = shade;
        ctx.font = 'bold 18px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(pl.label, pl.x + PLATFORM_W / 2, pl.y - 22);
        ctx.restore();
      }

      // Player
      const tilt = Math.max(-0.5, Math.min(0.9, g.playerVy / 700));
      const blink = g.hitCooldown > 0 && Math.floor(g.hitCooldown * 16) % 2 === 0;
      if (!blink) {
        drawSlingu(ctx, PLAYER_X, g.playerY, {
          radius: PLAYER_R,
          angle: tilt,
          blink: g.blink,
          wingPhase: g.wingPhase,
          mouth: g.playerVy < 0 ? 'open' : 'smile',
          groundY: GROUND_Y,
        });
      }

      // Particles
      for (const p of g.particles) {
        const a = Math.max(0, Math.min(1, p.life));
        ctx.save();
        ctx.globalAlpha = a;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      // Prompt banner
      const promptText = promptRef.current;
      if (promptText) {
        ctx.save();
        const bw = Math.min(WORLD_W - 24, 440);
        const by = 72;
        ctx.fillStyle = 'rgba(255,255,255,0.94)';
        roundRect(ctx, WORLD_W / 2 - bw / 2, by, bw, 46, 23);
        ctx.fill();
        ctx.strokeStyle = 'rgba(20, 83, 163, 0.2)';
        ctx.lineWidth = 1.5;
        roundRect(ctx, WORLD_W / 2 - bw / 2, by, bw, 46, 23);
        ctx.stroke();
        ctx.fillStyle = 'rgb(20, 83, 163)';
        ctx.font = 'bold 20px system-ui, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(promptText, WORLD_W / 2, by + 23);
        ctx.restore();
      }
    }

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      disposed = true;
      canvas.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', resize);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      startedRef.current = false;
    };
  }, [phase]);

  return (
    <div className="sl-minigame sl-minigame--portrait">
      <button type="button" className="sl-minigame__close"
        onClick={() => onExitRef.current({ score: 0, caught: 0, missed: 0, xp: 0 })}
        aria-label="Fechar jogo"
      >×</button>
      <div className="sl-flappy__sfx"><SfxToggle variant="icon" /></div>

      <canvas ref={canvasRef} className="sl-minigame__canvas" />

      <div className="sl-flappy__hud">
        <span className="sl-quizrun__hearts">{'♥'.repeat(hud.hearts)}{'·'.repeat(Math.max(0, 3 - hud.hearts))}</span>
        <span className="sl-quizrun__score">{hud.score} XP</span>
      </div>

      {phase === 'menu' ? (
        <div className="sl-flappy__menu">
          <h2><span aria-hidden>🦘</span> Word Jumper</h2>
          <p>
            Toque pra pular. Duas plataformas flutuam em alturas diferentes —
            uma tem a tradução <strong style={{ color: 'rgb(88,204,2)' }}>certa</strong>
            , outra não. Pouse errado, explode. <strong>3 explosões = fim</strong>.
            Pool de 1000+ palavras.
          </p>
          {highScore > 0 ? (
            <p className="sl-flappy__record">
              🏆 Seu recorde: <strong>{highScore} XP</strong>
            </p>
          ) : null}
          <div className="sl-flappy__menu-actions">
            <button type="button" className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
              onClick={() => { sfx.play('start'); setNewRecord(false); setPhase('playing'); }}
            >Começar</button>
          </div>
        </div>
      ) : null}

      {phase === 'over' ? (
        <div className="sl-flappy__over">
          <h2>{newRecord ? '🏆 Novo recorde!' : hud.caught > hud.missed ? 'Saltou bonito!' : 'Quase!'}</h2>
          <p>
            Acertos: <strong>{hud.caught}</strong> · Explosões: <strong>{hud.missed}</strong><br />
            XP: <strong>{hud.score}</strong>
          </p>
          <p className="sl-flappy__record">
            🏆 Recorde: <strong>{highScore} XP</strong>
            {newRecord ? ' ✨' : null}
          </p>
          <button type="button" className="sl-btn sl-btn--primary sl-btn--lg sl-btn--upper"
            onClick={() => onExitRef.current({ score: hud.score, caught: hud.caught, missed: hud.missed, xp: hud.score })}
          >Voltar pro app</button>
        </div>
      ) : null}
    </div>
  );
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, Math.abs(w) / 2, Math.abs(h) / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}
